import { Text, TextHeavy, UnderlineText } from 'components/RevxTypography';
import { useTranslation } from 'react-i18next';
import { JobItem } from 'domains/workspaceDomain';
import { Divider, message, Tooltip } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import RevxTextWithTooltip from 'components/RevxTextWithTooltip';
import RevxFlexLayout from 'components/RevxFlexLayout';
import useShowBillingContactFlag from 'features/Workspace/hooks/useShowBillingContactFlag';

type CardInfoContactsProps = JobItem & {
  // opens the Shipping & Billing tab of the card, used when the job has more than one oracle billing code
  onViewShippingBilling?: () => void;
};

const CardInfoContacts = (jobData?: CardInfoContactsProps) => {
  const { t } = useTranslation();
  const showBillingContact = useShowBillingContactFlag();

  const copyOracleBillingCode = (code?: string) => {
    if (!code) return;
    navigator.clipboard?.writeText(code).then(
      () => message.success(t('workspace.list.oracleBillingCodeCopied')),
      () => message.error(t('workspace.list.oracleBillingCodeCopyFailed'))
    );
  };

  if (!jobData) return <></>;
  const {
    billingInfo: { accountingLine, billtoCostCenter, billingContact },
    oracleBillingCode,
    multipleOracleBillingCode,
    onViewShippingBilling,
  } = jobData;
  return (
    <>
      {showBillingContact && (
        <RevxFlexLayout
          className="workspace-item-content-row"
          style={{ borderBottom: 'none' }}
        >
          <RevxFlexLayout>
            <TextHeavy className="workspace-item-content-row-title">
              {t('workspace.list.billingContact')}:
            </TextHeavy>
            <Text className="workspace-item-content-row-t">
              <RevxTextWithTooltip text={billingContact} />
            </Text>
          </RevxFlexLayout>
          <Divider type="vertical" />
          <RevxFlexLayout>
            <TextHeavy className="workspace-item-content-row-title">
              {t('workspace.list.costCenter')}:
            </TextHeavy>
            <Text className="workspace-item-content-row-t">
              <RevxTextWithTooltip text={billtoCostCenter} />
            </Text>
          </RevxFlexLayout>
          <Divider type="vertical" />
          <RevxFlexLayout>
            <TextHeavy className="workspace-item-content-row-title">
              {t('workspace.list.accountingLine')}:
            </TextHeavy>
            <Text className="workspace-item-content-row-t">
              <RevxTextWithTooltip text={accountingLine} />
            </Text>
          </RevxFlexLayout>
        </RevxFlexLayout>
      )}
      {(multipleOracleBillingCode || !!oracleBillingCode) && (
        <RevxFlexLayout
          className="workspace-item-content-row"
          align="top"
          style={{ borderBottom: 'none' }}
        >
          <TextHeavy className="workspace-item-content-row-title">
            {t('workspace.list.oracleBillingCode')}:
          </TextHeavy>
          {multipleOracleBillingCode ? (
            <UnderlineText
              size="small"
              color="blue"
              className="workspace-item-content-row-t"
              style={{ cursor: 'pointer' }}
              onClick={onViewShippingBilling}
            >
              {t('workspace.list.viewOracleBillingCodes')}
            </UnderlineText>
          ) : (
            <>
              {/* shown in full, the code wraps instead of being cut off with an ellipsis */}
              <Text
                className="workspace-item-content-row-t"
                style={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-all',
                  flex: '0 1 auto',
                  minWidth: 0,
                }}
              >
                {oracleBillingCode}
              </Text>
              <Tooltip title={t('workspace.list.copyOracleBillingCode')}>
                <CopyOutlined
                  style={{ marginLeft: 8, flexShrink: 0, cursor: 'pointer' }}
                  onClick={() => copyOracleBillingCode(oracleBillingCode)}
                />
              </Tooltip>
            </>
          )}
        </RevxFlexLayout>
      )}
    </>
  );
};

export default CardInfoContacts;
